"use client";

import { useTranslations } from "next-intl";
import { uploadDocuments } from "@/api/user/registrationDocumentUpload";
import { RegisterContext } from "@/context";
import { useParams, useRouter } from "next/navigation";
import { useContext, useRef, useState, useEffect } from "react";
import { CheckCircleRounded } from "@mui/icons-material";
import ArrowBackwardRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import { Alert, Button, Card, Checkbox, Divider, FormControlLabel, Typography, useMediaQuery, useTheme } from "@mui/material";
import NavigationLink from "@/common/navigation-link.common";
import UploadFile from "@/common/upload-file.common";

export const DocumentUpload = () => {
  const t = useTranslations();
  const router = useRouter();
  const { locale } = useParams() ?? {};
  const { activeStep, setActiveStep, userToken } = useContext(RegisterContext);
  const [aadharFiles, setAadharFiles] = useState([]);
  const [addressFiles, setAddressFiles] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const topRef = useRef(null);
  
  // Add theme and media query for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const aadharFile = aadharFiles[0];
  const addressFile = addressFiles[0];
  
  console.log("User Token:", userToken);
  
  const onClickVerify = async () => {
    console.log("Button clicked, starting document upload...");
    console.log("Aadhar File:", aadharFile);
    console.log("Address File:", addressFile);
    setError("");

    // Validation: Check if both files are uploaded
    if (!aadharFile || !addressFile) {
      setError("Please upload both Aadhaar and PAN.");
      topRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
      console.log("Error: Missing files");
      return;
    }

    // Validation: Check if user agrees to the terms
    if (!agreed) {
      setError("You must agree to the terms and conditions.");
      topRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
      return;
    }
    
    try {
      setIsUploading(true); // Start uploading state
      const response = await uploadDocuments({
        userToken,
        aadharFile,
        addressFile
      });
      
      if (response.message === "Documents uploaded successfully!") {
        setSuccessMessage("Registration successful");
        router.push(`/${locale}/login`);
      }
    } catch (uploadError) {
      setError(uploadError.message || "Upload failed.");
      topRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  const onClickBack = () => {
    setActiveStep((prev) => prev - 2);
  };
  
  return (
    <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-8`}>
      <div className="w-full flex flex-col gap-8">
        <Typography 
          ref={topRef} 
          variant={isMobile ? "h5" : "h4"} 
          sx={{ lineHeight: "1" }}
        >
          {t("Document Upload")}
        </Typography>

        <Typography 
          sx={{ color: "#4B5563", fontSize: isMobile ? "14px" : "16px" }} 
          variant="body1"
        >
          {t("uploadKYCcopies")}
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}
        {isUploading && <Alert severity="info">{t("Uploading documents")}</Alert>}

        <div className="w-full flex flex-col gap-6">
          <UploadFile heading={t("Upload Aadhaar*")} files={aadharFiles} onUploadFile={(files) => setAadharFiles(files)} />
          <UploadFile heading={t("Upload PAN*")} files={addressFiles} onUploadFile={(files) => setAddressFiles(files)} />

          <Card elevation={1} sx={{ paddingBottom: "2rem", padding: "1rem" }}>
            <FormControlLabel 
              sx={{ alignItems: "flex-start" }} 
              control={
                <Checkbox 
                  value="terms" 
                  color="primary" 
                  checked={agreed} 
                  onChange={(e) => setAgreed(e.target.checked)} 
                  sx={{ marginTop: "4px" }} 
                />
              } 
              label={
                <div className={`flex gap-2 ${isMobile ? 'text-sm' : ''}`}>
                  {t("FinalDeclaration")}
                  <Typography variant="h6" color="red">*</Typography>
                </div>
              } 
            />
          </Card>

          <div className="flex justify-between">
            <Button 
              variant="text" 
              startIcon={<ArrowBackwardRoundedIcon />} 
              onClick={onClickBack}
              size={isMobile ? "small" : "medium"}
            >
              {t("Back")}
            </Button>
            <Button 
              variant="contained" 
              onClick={onClickVerify} 
              disabled={isUploading}
              size={isMobile ? "small" : "medium"}
            >
              {isUploading ? "Uploading..." : t("Complete Registration")}
            </Button>
          </div>
        </div>
      </div>

      {/* Side panel - becomes full width on mobile */}
      <div className={isMobile ? "mt-8 w-full" : ""}>
        <Card elevation={1} sx={{ padding: "1rem" }}>
          <Typography variant={isMobile ? "subtitle1" : "h6"}>
            {t("Document Guidelines")}
          </Typography>
          
          <Typography 
            sx={{ display: "flex", gap: "0.5rem", fontSize: isMobile ? "14px" : "16px", alignItems: "flex-start", my: 1 }} 
            color="grey" 
            variant="body1"
          >
            <CheckCircleRounded color="primary" style={{ minWidth: 24 }} />
            {t("validAddressProof")}
          </Typography>
          
          <Typography 
            sx={{ display: "flex", gap: "0.5rem", fontSize: isMobile ? "14px" : "16px", alignItems: "flex-start", my: 1 }} 
            color="grey" 
            variant="body1"
          >
            <CheckCircleRounded color="primary" style={{ minWidth: 24 }} />
            {t("Documents should be clear and readable")}
          </Typography>
          
          <Typography 
            sx={{ display: "flex", gap: "0.5rem", fontSize: isMobile ? "14px" : "16px", alignItems: "flex-start", my: 1 }} 
            color="grey" 
            variant="body1"
          >
            <CheckCircleRounded color="primary" style={{ minWidth: 24 }} />
            {t("Address proof should not be older than 3 months")}
          </Typography>
        </Card>

        <Card sx={{ marginTop: "1.5rem", padding: "1rem" }} elevation={1}>
          <Typography variant={isMobile ? "subtitle1" : "h6"}>
            {t("Frequently Asked Questions")}
          </Typography>
          
          <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px", mt: 1 }} variant="body1">
            {t("What if my document is not clear")}
          </Typography>
          
          <Typography sx={{ color: "#4B5563", fontSize: isMobile ? "14px" : "16px", mb: 1 }} variant="body1">
            {t("HighResolutionCopyUpload")}
          </Typography>
          
          <Divider />

          <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px", mt: 1 }} variant="body1">
            {t("How do I compress my file size")}
          </Typography>
          
          <Typography sx={{ color: "#4B5563", fontSize: isMobile ? "14px" : "16px", mb: 1 }} variant="body1">
            {t("OnlineToolsToCompressFiles")}
          </Typography>
          
          <Divider />

          <Typography sx={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px", mt: 1 }} variant="body1">
            {t("Need more help")}
          </Typography>
          
          <Typography sx={{ color: "#4B5563", fontSize: isMobile ? "14px" : "16px" }} variant="body1">
            <NavigationLink href="#">
              <div className="flex items-center gap-2">
                <SupportAgentRoundedIcon fontSize={isMobile ? "small" : "medium"} />
                {t("Contact Support")}
              </div>
            </NavigationLink>
          </Typography>
        </Card>
      </div>
    </div>
  );
};

export default DocumentUpload;